import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
  Renderer2,
  ApplicationRef,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { filter, Subject, takeUntil } from 'rxjs';

import { DialogConfirmComponent } from '@app/shared/components/dialog-confirm/dialog-confirm.component';
import { MatStepper } from '@angular/material/stepper';
import { phoneValidator } from '@app/shared/helpers/validators.helper';
import { DriverService } from '@app/services/driver.service';
import { InjuryDialogStateService } from '@app/shared/services/injury-dialog-state.service';
import {
  DialogPositionService,
  DialogType,
} from '@app/shared/services/dialog-position.service';

export interface Item {
  id: string;
  name: string;
  state: boolean;
  x: number;
  y: number;
}

@Component({
  selector: 'app-dialog-new-injury',
  templateUrl: './dialog-new-injury.component.html',
  styleUrls: ['./dialog-new-injury.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true },
    },
  ],
})
export class DialogNewInjuryComponent implements OnInit, AfterViewInit, OnDestroy {
  step: number = 0;
  injuryForm!: FormGroup;
  maxStep: number = 6;
  isMinimized: boolean = false;
  savedFormData: any = null;
  minimizedDialogElement: HTMLElement | null = null;
  backdropStyles: any = { opacity: '', pointerEvents: '' };
  isSaving: boolean = false;
  private dialogPosition: { right: string; bottom: string } = {
    right: '280px',
    bottom: '0px',
  };
  private destroy$ = new Subject<void>();
  private dialogBackdrop: HTMLElement | null = null;
  private mutationObserver: MutationObserver | null = null;

  @ViewChild('stepper') stepper!: MatStepper;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<DialogNewInjuryComponent>,
    private driverService: DriverService,
    private renderer: Renderer2,
    private appRef: ApplicationRef,
    private ngZone: NgZone,
    private injuryDialogStateService: InjuryDialogStateService,
    private dialogPositionService: DialogPositionService
  ) {
    // Prevent dialog from closing when clicking outside
    this.dialogRef.disableClose = true;

    // Handle backdrop click with the same confirmation logic as the close button
    this.dialogRef.backdropClick().subscribe(() => {
      this.onDialogCloseClick();
    });

    // Add custom class to dialog panel for z-index control
    this.dialogRef.addPanelClass('dialog-new-injury-overlay-pane');
  }

  ngOnInit(): void {
    this.setInitForm();

    // Observe DOM changes to detect selection panels and datepickers
    this.observeOverlayChanges();

    this.dialogPositionService.minimizedDialogs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isMinimized) {
          this.dialogPosition = this.dialogPositionService.getPositionForDialog(
            DialogType.INJURY
          );
          this.updateMinimizedDialogPosition();
        }
      });

    this.dialogPositionService.screenResize$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isMinimized) {
          this.dialogPosition = this.dialogPositionService.getPositionForDialog(
            DialogType.INJURY
          );
          this.updateMinimizedDialogPosition();
        }
      });
  }

  setInitForm(): void {
    let driverId = '';
    if (this.data?.driver?.id) {
      driverId = this.data.driver.id;
    }

    let driverName = '';
    if (this.data?.driver?.driverName) {
      driverName = this.data.driver.driverName;
    }

    let organizationId = '';
    if (this.data?.driver?.organizationId) {
      organizationId = this.data.driver.organizationId;
    }
    this.injuryForm = this.fb.group({
      driverInfo: this.fb.group({
        organization: [organizationId, Validators.required],
        driverId: [{ value: '', disabled: true }, Validators.required],
        driverCode: [driverId],
        cellphone: ['', [Validators.required, phoneValidator()]],
        driverName: [{ value: driverName, disabled: true }],
        terminal: [{ value: null, disabled: true }],
        fleetManager: [{ value: null, disabled: true }],
        safetyManager: [{ value: null, disabled: true }],
      }),
      timePlace: this.fb.group({
        dateReport: ['', Validators.required],
        timeReport: ['', Validators.required],
        dateInjury: ['', Validators.required],
        timeInjury: ['', Validators.required],
        addressLine1: [''],
        addressLine2: [''],
        city: ['', Validators.required],
        state: ['', Validators.required],
        zip: [''],
        otherLocationDetails: [''],
      }),
      injuryDetail: this.fb.group({
        injuryReported: [null, Validators.required],
        injuryReportedTo: [null],
        dateInjuryReported: [null],
        timeInjuryReported: [null],
        howInjuryOccur: [null, Validators.required],
      }),
      injuryDescription: this.fb.group({
        areaBodyInjured: [null, Validators.required],
        extentInjuries: [null, Validators.required],
        medicalAssistanceRequired: [null],
        medicalAssistanceReceived: [null],
      }),
      additionalInfo: this.fb.group({
        winterCleats: [null, Validators.required],
        witness: [null, Validators.required],
        witnessInformation: [null],
      }),
      leave: this.fb.group({
        leaveWork: [null, Validators.required],
        dateLeaveWork: [null],
        employeeReturned: [null],
        dateEmployeeReturned: [null],
      }),
    });
  }

  get driverInfoFormGroup(): FormGroup {
    return this.injuryForm.get('driverInfo') as FormGroup;
  }

  get driverNameValue(): string {
    return this.driverInfoFormGroup.get('driverName')?.value;
  }

  get timePlaceFormGroup(): FormGroup {
    return this.injuryForm.get('timePlace') as FormGroup;
  }

  get dateReport(): string {
    return this.timePlaceFormGroup.get('dateReport')?.value;
  }

  get dateInjury(): string {
    return this.timePlaceFormGroup.get('dateInjury')?.value;
  }

  get injuryDetailFormGroup(): FormGroup {
    return this.injuryForm.get('injuryDetail') as FormGroup;
  }

  get injuryDescriptionFormGroup(): FormGroup {
    return this.injuryForm.get('injuryDescription') as FormGroup;
  }

  get additionalInfoFormGroup(): FormGroup {
    return this.injuryForm.get('additionalInfo') as FormGroup;
  }

  get leaveFormGroup(): FormGroup {
    return this.injuryForm.get('leave') as FormGroup;
  }

  ngAfterViewInit(): void {
    this.stepper.selectedIndex = this.step;

    setTimeout(() => {
      const backdrops = this.document.querySelectorAll('.cdk-overlay-backdrop');
      if (backdrops.length > 0) {
        this.dialogBackdrop = backdrops[backdrops.length - 1] as HTMLElement;
      }

      // Set high z-index for dialog elements
      this.setHighZIndexForDialogElements();
    }, 0);
  }

  // Method to set high z-index for dialog elements
  private setHighZIndexForDialogElements(): void {
    // Find the specific wrapper for this dialog using the custom class
    const dialogContainer = this.document.querySelector(
      '.dialog-new-injury-overlay-pane'
    );

    if (dialogContainer) {
      // Set z-index for dialog panel
      this.renderer.setStyle(dialogContainer, 'z-index', '1001');

      // Find the parent overlay wrapper
      let parentElement = dialogContainer.parentElement;
      while (
        parentElement &&
        !parentElement.classList.contains('cdk-global-overlay-wrapper')
      ) {
        parentElement = parentElement.parentElement;
      }

      // If we find the wrapper, set its z-index
      if (parentElement) {
        this.renderer.setStyle(parentElement, 'z-index', '1001');

        // Find the corresponding backdrop (it will be the one with the same aria-modal attribute)
        const dialogId = dialogContainer.getAttribute('id');
        if (dialogId) {
          const backdrops = this.document.querySelectorAll('.cdk-overlay-backdrop');
          backdrops.forEach((backdrop) => {
            // Check if this backdrop is associated with our dialog
            if (this.isBackdropForDialog(backdrop as HTMLElement, dialogId)) {
              this.renderer.setStyle(backdrop, 'z-index', '1000');
            }
          });
        }
      }

      // Set z-index for elements .cdk-overlay-connected-position-bounding-box
      // That may be related to this dialog (for dropdowns)
      this.updateConnectedOverlayBoundingBoxes();
    }
  }

  // Method to update the z-index of the bounding boxes for dropdowns
  private updateConnectedOverlayBoundingBoxes(): void {
    const boundingBoxes = this.document.querySelectorAll(
      '.cdk-overlay-connected-position-bounding-box'
    );

    boundingBoxes.forEach((box) => {
      // Check if this bounding box belongs to a dropdown related to our dialog
      if (this.isMinimized) {
        // If the dialog is minimized, restore the original z-index
        this.renderer.setStyle(box, 'z-index', '1000');
      } else {
        // If the dialog is active, set high z-index
        this.renderer.setStyle(box, 'z-index', '1001');
      }
    });
  }

  onMinimizeClick(): void {
    this.savedFormData = this.injuryForm.getRawValue();
    this.isMinimized = true;
    this.dialogRef.addPanelClass('minimized');
    this.manipulateBackdrops(true);

    // Restore normal z-index in bounding boxes when minimizing
    this.updateConnectedOverlayBoundingBoxes();

    this.injuryDialogStateService.setDialogMinimized(true);
    this.dialogPositionService.setDialogMinimized(DialogType.INJURY, true);

    this.dialogPosition = this.dialogPositionService.getPositionForDialog(
      DialogType.INJURY
    );

    this.ngZone.run(() => {
      this.createMinimizedDialogElement();
    });
  }

  onMaximizeClick(): void {
    if (this.minimizedDialogElement) {
      this.renderer.addClass(this.minimizedDialogElement, 'slide-down');

      setTimeout(() => {
        this.isMinimized = false;
        this.dialogRef.removePanelClass('minimized');
        this.manipulateBackdrops(false);

        if (this.savedFormData) {
          this.injuryForm.patchValue(this.savedFormData);
        }

        this.injuryDialogStateService.setDialogMinimized(false);
        this.dialogPositionService.setDialogMinimized(DialogType.INJURY, false);

        this.removeMinimizedDialogElement();
        this.appRef.tick();

        // Restore high z-index for this dialog when maximizing
        setTimeout(() => this.setHighZIndexForDialogElements(), 0);
      }, 300);
    } else {
      this.isMinimized = false;
      this.dialogRef.removePanelClass('minimized');
      this.manipulateBackdrops(false);

      if (this.savedFormData) {
        this.injuryForm.patchValue(this.savedFormData);
      }

      this.injuryDialogStateService.setDialogMinimized(false);
      this.dialogPositionService.setDialogMinimized(DialogType.INJURY, false);
      this.appRef.tick();

      // Restore high z-index for this dialog when maximizing
      setTimeout(() => this.setHighZIndexForDialogElements(), 0);
    }
  }

  manipulateBackdrops(minimize: boolean): void {
    if (!this.dialogBackdrop) return;

    if (minimize) {
      this.backdropStyles.opacity = this.dialogBackdrop.style.opacity || '0.5';
      this.backdropStyles.pointerEvents =
        this.dialogBackdrop.style.pointerEvents || 'auto';

      this.renderer.setStyle(this.dialogBackdrop, 'opacity', '0');
      this.renderer.setStyle(this.dialogBackdrop, 'pointer-events', 'none');
    } else {
      this.renderer.setStyle(this.dialogBackdrop, 'opacity', this.backdropStyles.opacity);
      this.renderer.setStyle(
        this.dialogBackdrop,
        'pointer-events',
        this.backdropStyles.pointerEvents
      );
    }
  }

  createMinimizedDialogElement(): void {
    this.minimizedDialogElement = this.renderer.createElement('div');
    this.renderer.addClass(this.minimizedDialogElement, 'minimized-dialog');

    this.renderer.setStyle(
      this.minimizedDialogElement,
      'right',
      this.dialogPosition.right
    );
    this.renderer.setStyle(
      this.minimizedDialogElement,
      'bottom',
      this.dialogPosition.bottom
    );

    this.renderer.listen(this.minimizedDialogElement, 'click', (event) => {
      this.ngZone.run(() => {
        this.onMaximizeClick();
      });
    });

    const content = this.renderer.createElement('div');
    this.renderer.addClass(content, 'minimized-content');

    const text = this.renderer.createElement('span');
    const driverId = this.driverInfoFormGroup.get('driverCode')?.value || '';

    if (driverId && driverId.trim() !== '') {
      text.textContent = `New Injury - ${driverId.toUpperCase()}`;
    } else {
      text.textContent = `New Injury`;
    }

    const maximizeIcon = this.renderer.createElement('i');
    this.renderer.addClass(maximizeIcon, 'fas');
    this.renderer.addClass(maximizeIcon, 'fa-up-right-and-down-left-from-center');
    this.renderer.addClass(maximizeIcon, 'maximize-icon');

    this.renderer.listen(maximizeIcon, 'click', (event) => {
      event.stopPropagation();
      this.ngZone.run(() => {
        this.onMaximizeClick();
      });
    });

    this.renderer.appendChild(content, text);
    this.renderer.appendChild(content, maximizeIcon);
    this.renderer.appendChild(this.minimizedDialogElement, content);

    this.renderer.appendChild(this.document.body, this.minimizedDialogElement);
  }

  removeMinimizedDialogElement(): void {
    if (this.minimizedDialogElement) {
      this.renderer.removeChild(this.document.body, this.minimizedDialogElement);
      this.minimizedDialogElement = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.removeMinimizedDialogElement();

    // Restore normal z-index in bounding boxes when destroying
    const boundingBoxes = this.document.querySelectorAll(
      '.cdk-overlay-connected-position-bounding-box'
    );
    boundingBoxes.forEach((box) => {
      this.renderer.setStyle(box, 'z-index', '1000');
    });

    // Disconnect the observer if it exists
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    if (this.isMinimized) {
      if (this.dialogBackdrop) {
        this.renderer.setStyle(
          this.dialogBackdrop,
          'opacity',
          this.backdropStyles.opacity
        );
        this.renderer.setStyle(
          this.dialogBackdrop,
          'pointer-events',
          this.backdropStyles.pointerEvents
        );
      }
    }

    this.injuryDialogStateService.setDialogMinimized(false);
    this.dialogPositionService.setDialogMinimized(DialogType.INJURY, false);
  }

  onDialogCloseClick(): void {
    if (this.isMinimized) {
      this.onMaximizeClick();
      return;
    }

    if (this.injuryForm.dirty) {
      const data = {
        title: 'Unsaved changes',
        text: 'You have unsaved changes. Are you sure you want to leave this page? The changes you made will not be saved.',
      };
      const dialogRef = this.dialog.open(DialogConfirmComponent, {
        data,
        width: '500px',
        panelClass: 'high-z-unsaved-dialog',
      });

      setTimeout(() => {
        const unsavedDialogContainer = this.document.querySelector(
          '.high-z-unsaved-dialog'
        );
        if (unsavedDialogContainer) {
          this.renderer.setStyle(unsavedDialogContainer, 'z-index', '1002');

          let unsavedWrapper = unsavedDialogContainer.parentElement;
          while (
            unsavedWrapper &&
            !unsavedWrapper.classList.contains('cdk-global-overlay-wrapper')
          ) {
            unsavedWrapper = unsavedWrapper.parentElement;
          }

          if (unsavedWrapper) {
            this.renderer.setStyle(unsavedWrapper, 'z-index', '1002');
          }

          const backdrops = this.document.querySelectorAll('.cdk-overlay-backdrop');
          if (backdrops.length > 0) {
            const lastBackdrop = backdrops[backdrops.length - 1];
            this.renderer.setStyle(lastBackdrop, 'z-index', '1001');
          }
        }
      }, 0);

      dialogRef
        .afterClosed()
        .pipe(filter((closeModal: boolean) => closeModal))
        .subscribe(() => {
          this.injuryDialogStateService.setDialogMinimized(false);
          this.dialogRef.close();
        });
    } else {
      this.injuryDialogStateService.setDialogMinimized(false);
      this.dialogRef.close();
    }
  }

  onDoneClick(): void {
    // Prevent multiple calls if already saving
    if (this.isSaving) {
      return;
    }

    this.isSaving = true; // Disable buttons during save operation
    const request = this.createRequest();
    this.driverService.upsertInjury(request).subscribe({
      next: () => {
        this.isSaving = false; // Re-enable buttons on success
        this.showSavedInjuryMessage();
      },
      error: (error) => {
        this.isSaving = false; // Re-enable buttons on error
        console.error('Error saving injury', error);
      },
    });
  }

  showSavedInjuryMessage(): void {
    const formattedDriverName = this.driverNameValue
      .toLowerCase()
      .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase());
    const data = {
      title: 'Injury Created',
      text: `An injury for ${formattedDriverName} has been successfully created. The details captured can be found on the driver\'s activity section.`,
      confirm: true,
    };
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      data,
      width: '500px',
      panelClass: 'high-z-created-dialog',
    });

    setTimeout(() => {
      const createdDialogContainer = this.document.querySelector(
        '.high-z-created-dialog'
      );
      if (createdDialogContainer) {
        this.renderer.setStyle(createdDialogContainer, 'z-index', '1002');

        let createdWrapper = createdDialogContainer.parentElement;
        while (
          createdWrapper &&
          !createdWrapper.classList.contains('cdk-global-overlay-wrapper')
        ) {
          createdWrapper = createdWrapper.parentElement;
        }

        if (createdWrapper) {
          this.renderer.setStyle(createdWrapper, 'z-index', '1002');
        }

        const backdrops = this.document.querySelectorAll('.cdk-overlay-backdrop');
        if (backdrops.length > 0) {
          const lastBackdrop = backdrops[backdrops.length - 1];
          this.renderer.setStyle(lastBackdrop, 'z-index', '1001');
        }
      }
    }, 0);

    dialogRef.afterClosed().subscribe(() => {
      this.dialogRef.close();
    });
  }

  createRequest(): any {
    const request: any = {};
    const mapping: any = {
      driverInfo: {
        driverId: 'driverId',
        driverCode: 'driverCode',
        cellphone: 'employeePhone',
        organization: 'organization',
      },
      timePlace: {
        dateReport: 'reportToWorkDate',
        dateInjury: 'injuryDate',
        addressLine1: 'injuryAddress1',
        addressLine2: 'injuryAddress2',
        city: 'injuryCity',
        state: 'injuryState',
        zip: 'injuryZip',
        otherLocationDetails: 'injuryLocationOtherDetails',
      },
      injuryDetail: {
        injuryReported: 'injuryReported',
        injuryReportedTo: 'injuryReportedTo',
        dateInjuryReported: 'injuryReportedDate',
        timeInjuryReported: 'injuryReportedTime',
        howInjuryOccur: 'injuryHowOccurred',
      },
      injuryDescription: {
        areaBodyInjured: 'injuryAreaOfBody',
        extentInjuries: 'extentOfInjuryDescription',
        medicalAssistanceRequired: 'medicalAssistanceRequired',
        medicalAssistanceReceived: 'medicalWhereReceived',
      },
      additionalInfo: {
        winterCleats: 'employeeWinterCleats',
        witness: 'witness',
        witnessInformation: 'witnessInformation',
      },
      leave: {
        leaveWork: 'employeeWorkLeave',
        dateLeaveWork: 'employeeWorkLeaveDate',
        employeeReturned: 'employeeWorkReturn',
        dateEmployeeReturned: 'employeeWorkReturnDate',
      },
    };

    for (const groupKey in this.injuryForm.controls) {
      const group = this.injuryForm.get(groupKey) as FormGroup;
      if (group && mapping[groupKey]) {
        for (const controlKey in group.controls) {
          const mappedKey = mapping[groupKey][controlKey];
          if (mappedKey) {
            let value = group.get(controlKey)?.value;
            if (value === 'true') {
              value = true;
            } else if (value === 'false') {
              value = false;
            }
            request[mappedKey] = value;
          }
        }
      }
    }
    request.injuryDate = this.timePlaceFormGroup.get('dateInjury')?.value
      ? this.convertToDatabaseFormat(
          this.timePlaceFormGroup.get('dateInjury')?.value,
          this.timePlaceFormGroup.get('timeInjury')?.value
        )
      : null;
    request.reportToWorkDate = this.timePlaceFormGroup.get('dateReport')?.value
      ? this.convertToDatabaseFormat(
          this.timePlaceFormGroup.get('dateReport')?.value,
          this.timePlaceFormGroup.get('timeReport')?.value
        )
      : null;
    request.injuryReportedDate = this.injuryDetailFormGroup.get('dateInjuryReported')
      ?.value
      ? this.convertToDatabaseFormat(
          this.injuryDetailFormGroup.get('dateInjuryReported')?.value,
          this.injuryDetailFormGroup.get('timeInjuryReported')?.value
        )
      : null;
    request.employeeWorkLeaveDate = request.employeeWorkLeaveDate
      ? this.convertToDatabaseFormat(request.employeeWorkLeaveDate)
      : null;
    request.employeeWorkReturnDate = request.employeeWorkReturnDate
      ? this.convertToDatabaseFormat(request.employeeWorkReturnDate)
      : null;
    request.injuryAreaOfBody = request.injuryAreaOfBody
      ? this.getInjuryAreaOfBodyNames(request.injuryAreaOfBody)
      : null;

    const storedData = localStorage.getItem('userData');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      request.UserId = parsedData.userInfo.objectId;
    }

    request.organizationId = request?.organization?.organizationId;
    delete request.organization;

    request.driverId = request.driverCode;
    delete request.driverCode;

    return request;
  }

  getInjuryAreaOfBodyNames(injuryAreaOfBody: any): string {
    const injuryAreaOfBodyCopy = injuryAreaOfBody.map((item: any) => ({
      ...item,
    }));
    return injuryAreaOfBodyCopy
      .filter((item: any) => item.state)
      .map((item: any) => item.name)
      .join(', ');
  }

  convertToDatabaseFormat(date: string, accidentTime?: string) {
    const [month, day, year] = date.split('/');
    const padZero = (value: any) => (value.length === 1 ? `0${value}` : value);

    if (accidentTime) {
      const [hoursAndMinutes, period] = accidentTime.split(' ');
      let [hours, minutes] = hoursAndMinutes.split(':');

      if (period === 'PM' && hours !== '12') {
        hours = (parseInt(hours) + 12).toString();
      } else if (period === 'AM' && hours === '12') {
        hours = '00';
      } else {
        hours = padZero(hours.toString());
      }
      minutes = padZero(minutes);
      return `${year}-${padZero(month)}-${padZero(day)} ${hours}:${minutes}:00`;
    }
    return `${year}-${padZero(month)}-${padZero(day)} 00:00:00`;
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.step = event.selectedIndex;
  }

  onSaveClick() {
    // Prevent multiple calls if already saving
    if (this.isSaving) {
      return;
    }

    this.onDoneClick();
  }

  nextStep(): void {
    if (this.step < 9) {
      this.step++;
      this.stepper.next();
    }
  }

  prevStep(): void {
    if (this.step > 0) {
      this.step--;
      this.stepper.previous();
    }
  }

  reset(): void {
    this.step = 0;
    this.stepper.reset();
  }

  updateMinimizedDialogPosition(): void {
    if (this.minimizedDialogElement) {
      this.renderer.setStyle(
        this.minimizedDialogElement,
        'right',
        this.dialogPosition.right
      );
      this.renderer.setStyle(
        this.minimizedDialogElement,
        'bottom',
        this.dialogPosition.bottom
      );
    }
  }

  // Method to observe changes in the overlay container and adjust z-index
  private observeOverlayChanges(): void {
    // Use MutationObserver to detect when new overlays are added (like selectors or datepickers)
    const overlayContainer = this.document.querySelector('.cdk-overlay-container');
    if (overlayContainer) {
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // If a new node has been added, check if it is related to our dialog
            setTimeout(() => {
              // Identify the dialog container
              const injuryDialogContainer = this.document.querySelector(
                '.dialog-new-injury-overlay-pane'
              );
              if (!injuryDialogContainer) return;

              // Find the parent wrapper
              let dialogWrapper = injuryDialogContainer.parentElement;
              while (
                dialogWrapper &&
                !dialogWrapper.classList.contains('cdk-global-overlay-wrapper')
              ) {
                dialogWrapper = dialogWrapper.parentElement;
              }
              if (!dialogWrapper) return;

              // Only modify z-index if the dialog is active (not minimized)
              if (!this.isMinimized) {
                // Set z-index of the wrapper
                this.renderer.setStyle(dialogWrapper, 'z-index', '1001');

                // Find selectors, autocomplete and datepickers inside or related to our dialog
                const selectPanels = this.document.querySelectorAll(
                  '.mat-mdc-select-panel'
                );
                const autocompletePanels = this.document.querySelectorAll(
                  '.mat-mdc-autocomplete-panel'
                );
                const datepickerContent = this.document.querySelectorAll(
                  '.mat-datepicker-content'
                );

                // Find elements .cdk-overlay-connected-position-bounding-box
                const boundingBoxes = this.document.querySelectorAll(
                  '.cdk-overlay-connected-position-bounding-box'
                );

                // Only set high z-index if they are related to our dialog
                selectPanels.forEach((panel) => {
                  if (
                    this.isElementRelatedToDialog(
                      panel as HTMLElement,
                      dialogWrapper as HTMLElement
                    )
                  ) {
                    this.renderer.setStyle(panel as HTMLElement, 'z-index', '1002');
                  }
                });

                autocompletePanels.forEach((panel) => {
                  if (
                    this.isElementRelatedToDialog(
                      panel as HTMLElement,
                      dialogWrapper as HTMLElement
                    )
                  ) {
                    this.renderer.setStyle(panel as HTMLElement, 'z-index', '1002');
                  }
                });

                datepickerContent.forEach((content) => {
                  if (
                    this.isElementRelatedToDialog(
                      content as HTMLElement,
                      dialogWrapper as HTMLElement
                    )
                  ) {
                    this.renderer.setStyle(content as HTMLElement, 'z-index', '1002');
                  }
                });

                // Set z-index for the bounding boxes
                boundingBoxes.forEach((box) => {
                  this.renderer.setStyle(box, 'z-index', '1001');
                });
              }
            }, 0);
          }
        });
      });

      this.mutationObserver.observe(overlayContainer, { childList: true, subtree: true });
    }
  }

  // Helper method to determine if an element is related to our dialog
  private isElementRelatedToDialog(
    element: HTMLElement,
    dialogWrapper: HTMLElement
  ): boolean {
    // If the element is inside the dialog wrapper
    if (dialogWrapper.contains(element)) {
      return true;
    }

    // If the element is one of the most recently added to the DOM and is inside an overlay pane
    // This is useful for datepickers and selectors that are added as sibling nodes
    const isRecentlyAdded =
      Array.from(this.document.querySelectorAll('.cdk-overlay-pane')).indexOf(
        element.closest('.cdk-overlay-pane') as HTMLElement
      ) >= 0;

    // If the user is interacting with our dialog (it is the active one),
    // we assume that the recently added UI elements are related to it
    return isRecentlyAdded && !this.isMinimized;
  }

  // Helper method to determine if a backdrop belongs to a specific dialog
  private isBackdropForDialog(backdrop: HTMLElement, dialogId: string): boolean {
    // In Angular Material, backdrops usually have a class attribute that relates them to the dialog
    // We can also use the position of the backdrop in the list of backdrops
    const backdropPosition = Array.from(
      this.document.querySelectorAll('.cdk-overlay-backdrop')
    ).indexOf(backdrop);
    const dialogPosition = Array.from(
      this.document.querySelectorAll('.cdk-overlay-pane')
    ).findIndex((pane) => pane.querySelector(`#${dialogId}`) !== null);

    // If they are in the same relative position, they probably belong to the same dialog
    return backdropPosition === dialogPosition;
  }
}
